import React, { useState, useEffect } from 'react';
import { withNamespaces } from 'react-i18next';
import get from 'lodash/get';
import { withSitecoreContext, Text } from '@sitecore-jss/sitecore-jss-react';
import Hidden from '@material-ui/core/Hidden';
import clsx from 'clsx';

import Grid from '../../../components/molecules/Grid';
import Container from '../../../components/molecules/Container';
import Typography from '../../../components/atoms/Typography';
import CategoryTag from '../../../components/atoms/CategoryTag';
import IconWithText from '../../../components/molecules/IconWithText';
import * as locators from './locators';
import Box from '../../../components/molecules/Box';
import { pageContentType } from '../../../utils/getContentType';
import { getDetailedPageTags } from '../../../utils/getDetailedPageTags';
import { getDictionaryText } from '../../../utils/getDictionaryText';
import { getCurrentLanguage } from '../../../utils/getCurrentLanguage';
import {
  getPageName,
  getComponentName,
} from '../../../utils/getPageComponentInfo';
import { analyticsData } from '../../../../constants/Analytics/analytics-data';

import {
  EVENT_TYPE,
  EVENTS_TAG_THEME_COLOR,
  HIGHLIGHTS_KEY,
} from '../../../../constants/index';
import useStyles from './style';
import RichText from '../../../components/atoms/RichText';
import { canUseDOM } from '../../../utils/canUseDOM';
import { useTheme } from '@material-ui/core/styles';
export const DetailedDescription = props => {
  const [expanded, setExpanded] = useState(false);
  const [expandBtn, setExpandBtn] = useState(false);
  const theme = useTheme();
  useEffect(() => {
    if (canUseDOM) {
      const tagContainer = document.querySelector(
        '.tagContainer .categoryTags'
      );
      const isMobile = window.matchMedia('(max-width: 767px)');
      const tagContainerHeight = tagContainer && tagContainer.offsetHeight;
      if (!isMobile.matches && tagContainerHeight > 42) {
        setExpandBtn(true);
      } else if (isMobile.matches && tagContainerHeight > 82) {
        setExpandBtn(true);
      }
    }
  }, [expandBtn]);

  const contentType = pageContentType(props).toLowerCase();
  const sectionName = get(
    props,
    'sitecoreContext.route.fields.pageHeading.value',
    ''
  )
    ? get(props, 'sitecoreContext.route.fields.pageHeading.value', '')
    : get(props, 'sitecoreContext.route.fields.name.value', '');
  let language = getCurrentLanguage(props);
  language = language && language.toLowerCase();
  const classes = useStyles({ EVENTS_TAG_THEME_COLOR, contentType });
  const route = get(props, 'sitecoreContext.route', '');
  const { description, highlights, subTitle } = (route && route.fields) || '';
  const finalDescription = contentType === EVENT_TYPE ? subTitle : description;
  const genericTags = get(
    props,
    'sitecoreContext.route.fields.genericTags',
    []
  );
  let highlightsLabel = getDictionaryText(
    `${pageContentType(props)}${HIGHLIGHTS_KEY}`
  );
  if (highlightsLabel === `${pageContentType(props)}${HIGHLIGHTS_KEY}`) {
    // Making sure that if dictionary key based on contentType is not present then fallback dictionary value will be served
    highlightsLabel = getDictionaryText(HIGHLIGHTS_KEY);
  }
  const getHighLightsLabel = () => (
    <Typography variant="h4" component="h2" className="heading-text">
      <Text field={{ value: highlightsLabel }} />
    </Typography>
  );
  const getEventDetailedPageTags = () => {
    const tags = [];
    const dictionaryTagKeys = getDictionaryText(
      `${pageContentType(props)}DetailTags`
    );
    const dictionaryTagKeyArray =
      dictionaryTagKeys && dictionaryTagKeys.split(',');
    dictionaryTagKeyArray &&
      dictionaryTagKeyArray.length > 0 &&
      dictionaryTagKeyArray.forEach(key => {
        const tagValueFromSitecore = get(
          props,
          `sitecoreContext.route.fields.${key}[0].fields.taxonomyValue.value`,
          ''
        );
        const tagKeyFromSitecore = get(
          props,
          `sitecoreContext.route.fields.${key}[0].fields.searchKey.value`,
          ''
        );
        tagValueFromSitecore &&
          tagKeyFromSitecore &&
          tags.push({ text: tagValueFromSitecore, link: tagKeyFromSitecore });
      });
    return tags;
  };

  const getDataId = () => {
    return analyticsData.dataIds.pageBodyNavLinkClick;
  };

  const getDataLink = buttonName => {
    return `${getPageName(props)} | ${getComponentName(
      props
    )} | ${sectionName} - ${buttonName}`;
  };

  const getAnalyticsData = buttonName => {
    return {
      dataId: getDataId(),
      dataLink: getDataLink(buttonName),
    };
  };

  const handleToggle = e => {
    setExpanded(!expanded);
    if (canUseDOM) {
      const tagContainer = document.querySelector('.tagContainer');

      if (tagContainer && tagContainer.classList.contains('showContent')) {
        e.target.classList.remove('less');
        tagContainer.classList.remove('showContent');
      } else {
        e.target.classList.add('less');
        tagContainer && tagContainer.classList.add('showContent');
      }
    }
  };

  const categoryTags =
    contentType === EVENT_TYPE
      ? getEventDetailedPageTags()
      : getDetailedPageTags(props);
  const getIconElements = (iconData, index) => {
    return (
      <IconWithText
        key={index}
        iconType='FallbackCheckIcon'
        iconImage={get(iconData, 'highlightType.fields.icon', '')}
        iconWithCircle={false}
        className={clsx(classes.iconWrapper, 'hightlight-icon')}
        variant="square"
      >
        <Box>
          {/* <Typography
            variant="labelRegular"
            component="p"
            className={classes.iconText}
          >
            <Text field={get(iconData, 'highlightType.fields.label', '')} />
          </Typography> */}
          <Typography variant="body1" component="p">
            <Text field={{ value: get(iconData, 'text.value', '') }} />
          </Typography>
        </Box>
      </IconWithText>
    );
  };
  return (
    <Container
      className={clsx(classes.root, locators.COMPONENT_NAME)}
      data-locator={locators.COMPONENT_NAME}
    >
      <Grid>
        <div>
          {finalDescription && finalDescription.value && (
            <Typography variant="body1" component="p">
              <RichText
                innerHTML={finalDescription}
                className={classes.richTextOverride}
              />
            </Typography>
          )}
        </div>
        <div className={clsx(classes.tagContainer, 'tagContainer')}>
          <div className="categoryTags">
            {categoryTags && categoryTags.length > 0 && (
              <Typography component="div" className={classes.categoryTags}>
                {categoryTags.map((item, index) => (
                  <CategoryTag
                    text={item.text}
                    key={index}
                    language={language}
                    attributeFilter={item.link}
                    analyticsData={getAnalyticsData(item.text)}
                    isDescriptionTag
                    borderStyle={contentType === EVENT_TYPE ? theme.borders.DarkTurquoiseBorder : ''}
                  />
                ))}
              </Typography>
            )}
            {genericTags && genericTags.length > 0 && (
              <Typography component="div" className={classes.categoryTags}>
                {genericTags.map((item, index) => (
                  <CategoryTag
                    text={get(item, 'fields.taxonomyValue.value')}
                    key={index}
                    isDescriptionTag
                    attributeFilter={item.link}
                    analyticsData={getAnalyticsData(get(item, 'fields.taxonomyValue.value'))}
                    language={language}
                  />
                ))}
              </Typography>
            )}
          </div>
        </div>
        {expandBtn && (
          <span
            className={clsx(classes.toggleIcon, 'toggleIcon')}
            onClick={handleToggle}
          >
            {expanded
              ? getDictionaryText('viewLessTags')
              : getDictionaryText('viewMoreTags')}
          </span>
        )}
        <div>
          {pageContentType !== EVENT_TYPE &&
            highlights &&
            highlights.length > 0 && (
              <>
                <Hidden lgUp implementation="css">
                  <div className={classes.highLightHeading}>
                    {highlightsLabel && getHighLightsLabel()}
                  </div>
                  {highlights.map((item, index) => {
                    return getIconElements(item.fields, index);
                  })}
                </Hidden>
                <Hidden mdDown implementation="css">
                  <Grid container className={classes.highLightHeading}>
                    {highlightsLabel && (
                      <Grid item lg={3}>
                        {getHighLightsLabel()}
                      </Grid>
                    )}
                    <Grid
                      item
                      container
                      lg={9}
                      className={classes.highLightList}
                    >
                      {highlights.slice(0, 6).map((item, index) => {
                        return (
                          <Grid item container lg={4} key={index}>
                            {getIconElements(item.fields, index)}
                          </Grid>
                        );
                      })}
                    </Grid>
                  </Grid>
                </Hidden>
              </>
            )}
        </div>
      </Grid>
    </Container>
  );
};

export default withSitecoreContext()(withNamespaces()(DetailedDescription));
